import type { FC } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, SvgIcon, Typography } from '@mui/material'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'

import { HOME_CONTENT } from '../defaults'

type MuiIcon = typeof SvgIcon

interface PromiseCard {
  readonly Icon: MuiIcon
  readonly iconColor: string
  readonly title: string
  readonly body: string
}

const PROMISE_CARDS: readonly PromiseCard[] = [
  { Icon: VisibilityOutlinedIcon, iconColor: 'info.main', ...HOME_CONTENT.promises[0] },
  { Icon: LockOutlinedIcon, iconColor: 'success.main', ...HOME_CONTENT.promises[1] },
  { Icon: HistoryOutlinedIcon, iconColor: 'warning.light', ...HOME_CONTENT.promises[2] },
]

export const PromisesSection: FC = () => (
  <Box sx={sectionContainerSx}>
    <Box sx={cardsContainerSx}>
      {PROMISE_CARDS.map(({ Icon, iconColor, title, body }) => (
        <Box key={title} sx={cardSx}>
          <Typography sx={cardTitleSx}>{title}</Typography>
          <Typography variant="body2" sx={cardBodySx}>
            {body}
          </Typography>
          <Icon aria-hidden="true" sx={buildIconSx(iconColor)} />
        </Box>
      ))}
    </Box>
  </Box>
)

const sectionContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  py: { xs: 4, md: 6 },
  px: { xs: 2, md: 4 },
}

const cardsContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: { xs: 4, md: 6 },
  width: '100%',
  maxWidth: 1200,
}

const cardSx: SxProps<Theme> = (theme) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  flex: { xs: '1 1 100%', md: '1 1 0' },
  maxWidth: 420,
  px: { xs: 2.5, md: 4 },
  py: { xs: 3.5, md: 5 },
  borderRadius: 2,
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(62, 39, 35, 0.03)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.06)'
      : '1px solid rgba(62, 39, 35, 0.08)',
})

const buildIconSx = (color: string): SxProps<Theme> => ({
  color,
  opacity: 0.7,
  fontSize: 48,
  mt: 2,
})

const cardTitleSx: SxProps<Theme> = {
  color: 'text.primary',
  fontSize: { xs: '1.1rem', md: '1.25rem' },
  fontWeight: 600,
  mb: 1,
}

const cardBodySx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.95rem',
  lineHeight: 1.6,
}
